import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Divider, FormControl, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useGetLocationsQuery } from '../../../../app/redux-fetch/apiCoreQuery';
import { useGetAllLoadTypesQuery, useGetServicesQuery } from '../../../../app/redux-fetch/apiOrders';
import MRTDataGridv2 from '../../../../components/MRTDataGrid/MRTDataGridv2';
import { TableSubTitleTypography } from '../../../../components/Typographies/TableTitleTypography';
import { MRTColumnDef } from '../../../../types/common';
import { useOrderFormContext } from '../../context/OrderFormContext';
import messages from '../messages';
import { ServiceWaypoint, WaypointType } from '../types';
import { mapServiceForDisplay } from '../utils';

export interface ConsignmentServiceTableProps {
    consignmentIndex: number;
    waypointType: WaypointType;
    waypointPrefix: string;
    loadId?: number;
}

const ConsignmentServiceTable = ({ consignmentIndex, waypointPrefix, loadId }: ConsignmentServiceTableProps) => {
    const { formatMessage } = useIntl();
    const { watch, setValue, getValues } = useFormContext();
    const { variant } = useOrderFormContext();

    const tenantId = watch('tenantId');
    const customerId = watch('customerId');
    const isViewMode = variant === 'summary';
    const { data: locations = [] } = useGetLocationsQuery({ tenantId, customerId }, { skip: isViewMode });

    const {
        data: services = [],
    } = useGetServicesQuery({ tenantId }, { skip: isViewMode })

    const { data: packagingTypes = [] } = useGetAllLoadTypesQuery(undefined, { skip: isViewMode })

    const loads = watch(`bookings.${consignmentIndex}.packaging`);
    const loadTypeId = watch(`bookings.${consignmentIndex}.loadTypeId`);

    const getContainerType = useCallback((templateId: number) => {
        return packagingTypes.find((x: any) => x.id === templateId)?.name ?? 'N/A';
    }, [packagingTypes]);




    const waypointId = watch(`bookings.${consignmentIndex}.${waypointPrefix}.id`);


    const getAvailableServices = (loadTypeId: number | string, loadId: number) => {

        const waypointServices = getValues(`bookings.${consignmentIndex}.${waypointPrefix}.services`) ?? [];


        return services.filter((service: any) =>
            service.enabled &&
            service.customerSelectable &&
            service.loadTypes.some((lt: any) => lt.loadTypeId === Number(loadTypeId)) &&
            !waypointServices.some((ss: any) =>
                ss.serviceId === service.id &&
                Array.isArray(ss.loads) &&
                ss.loads.some((sl: any) => sl.loadId === loadId)
            )
        );
    };

    const loadData = useMemo(() => {
        return loads.map((load: any, loadIndex: number) => ({
            ...load,
            loadIndex,
            bookingIndex: consignmentIndex,
            loadTypeId,
            loadTypeName: packagingTypes.find((lt: any) => lt.id === loadTypeId)?.name,
            dimensions: `${load.length} x ${load.width} x ${load.height}`,
            weight: load.weight,
        }));
    }, [loads, loadTypeId, packagingTypes]);


    const handleAddService = (loadId: number, serviceId: number) => {

        const waypointServices = getValues(`bookings.${consignmentIndex}.${waypointPrefix}.services`) ?? [];

        const newService = {
            waypointId: waypointId,
            serviceId,
            loads: [{
                loadId
            }]
        };

        setValue(`bookings.${consignmentIndex}.${waypointPrefix}.services`, [...waypointServices, newService], {
            shouldDirty: true,
        });
    };

    const handleDeleteService = (serviceIndex: number) => {
        const waypointServices = getValues(`bookings.${consignmentIndex}.${waypointPrefix}.services`) ?? [];
        waypointServices.splice(serviceIndex, 1);
        setValue(`bookings.${consignmentIndex}.${waypointPrefix}.services`, waypointServices, {
            shouldDirty: true,
        });
    };

    const columns = useMemo<MRTColumnDef<any>[]>(() => [
        {
            accessorKey: 'reference',
            header: 'Load',
            size: 150,
            Cell: ({ row }: any) => (
                <Typography variant="body2">
                    Load #{row.original.loadIndex + 1}
                </Typography>
            )
        },
        {
            accessorKey: 'loadTypeId',
            header: 'Type',
            size: 120,
            Cell: ({ row }: any) => (
                <Typography variant="body2">
                    {getContainerType(loadTypeId)}
                </Typography>
            )
        },
        {
            accessorKey: 'quantity',
            header: 'Quantity',
            Cell: ({ row }: any) => (
                <Typography variant="body2">
                    {row.original.numberOfPackages}
                </Typography>
            )
        },
        {
            accessorKey: 'serviceSelection',
            header: 'Services',
            size: 200,
            Cell: ({ row }: { row: any }) => {
                const { id: loadId, loadTypeId } = row.original;
                const availableServices = getAvailableServices(loadTypeId, loadId);

                return (
                    <Box>
                        <FormControl fullWidth size="small">
                            <Select
                                displayEmpty
                                disabled={availableServices.length === 0}
                                value=""
                                MenuProps={{
                                    PaperProps: { sx: { maxHeight: 200 } }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                    },
                                    p: '2px 0 0 0',
                                    m: 0,
                                    alignItems: 'center',
                                    '& .MuiSelect-select': {
                                        p: '2px 0 0 0',
                                        m: 0,
                                    }
                                }}
                                renderValue={(selected) => {
                                    return <Typography variant="body2">
                                        {availableServices.length === 0 ? 'No services available' : 'Select a service'}
                                    </Typography>
                                }}
                            >
                                {availableServices.map((service: any) => (
                                    <MenuItem
                                        key={service.id}
                                        value={service.id.toString()}
                                        onClick={() => {
                                            handleAddService(loadId, service.id);
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {`${service.name} - (${service.code})`}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                );
            },
        },
        {
            accessorKey: 'selectedServices',
            header: 'Selected Services',
            size: 250,
            Cell: ({ row }: { row: any }) => {
                const waypointServices = getValues(`bookings.${consignmentIndex}.${waypointPrefix}.services`) ?? [];

                if (waypointServices.length === 0) return <Typography variant="body2" >No services selected</Typography>;

                const loadServices = waypointServices
                    .filter((service: ServiceWaypoint) =>
                        Array.isArray(service.loads) &&
                        service.loads.some((load) => load.loadId === row.original.id)
                    )
                    .map((service: ServiceWaypoint) => mapServiceForDisplay(service, services))
                    .filter(Boolean);

                if (loadServices.length === 0) return <Typography variant="body2" >No services selected</Typography>;

                return (
                    <Stack spacing={1} divider={<Divider />}>
                        {loadServices.map((service: any, idx: number) => {
                            return (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body2">
                                            {`${service.serviceName} - (${service.serviceCode})`}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        onClick={() => {
                                            const serviceIndex = waypointServices.findIndex(
                                                (s: ServiceWaypoint) => s.serviceId === service.serviceId &&
                                                    Array.isArray(s.loads) &&
                                                    (s.loads.some(l => l.loadId === row.original.loadIndex)
                                                        || s.loads.some(l => l.loadId === row.original.id))
                                            );

                                            if (serviceIndex !== -1) {
                                                handleDeleteService(serviceIndex);
                                            }
                                        }}
                                        size="small"
                                        color="error"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Stack>
                );
            },
        },
    ], [services, getContainerType, watch, getValues, setValue]);

    if (variant === 'summary') {
        const allWaypointServices: any[] = [];

        const shipFromServices = getValues(`bookings.${consignmentIndex}.shipFrom.services`) ?? [];
        const shipFromLocationId = watch(`bookings.${consignmentIndex}.shipFrom.locationId`);

        const shipFromLocation = locations.find((l: any) => l.id === shipFromLocationId);


        shipFromServices.forEach((service: any) => {
            const isLoadService = service.loads?.some((load: any) => load.loadId === loadId);
            if (!isLoadService) return;

            allWaypointServices.push({
                ...service,
                waypointType: 'shipFrom',
                waypointName: shipFromLocation?.name || formatMessage(messages.shipFrom),
                locationName: shipFromLocation?.name || formatMessage(messages.shipFrom)
            });
        });

        const deliverToServices = getValues(`bookings.${consignmentIndex}.deliverTo.services`) ?? [];
        const deliverToLocationId = watch(`bookings.${consignmentIndex}.deliverTo.locationId`);

        const deliverToLocation = locations.find((l: any) => l.id === deliverToLocationId);

        deliverToServices.forEach((service: any) => {
            const isLoadService = service.loads?.some((load: any) => load.loadId === loadId);
            if (!isLoadService) return;

            allWaypointServices.push({
                ...service,
                waypointType: 'deliverTo',
                waypointName: deliverToLocation?.name || formatMessage(messages.deliverTo),
                locationName: deliverToLocation?.name || formatMessage(messages.deliverTo)
            });
        });

        const waypoints = getValues(`bookings.${consignmentIndex}.waypoints`) ?? [];

        waypoints.forEach((waypoint: any, waypointIndex: number) => {


            const waypointServices = waypoint.services ?? [];
            const waypointLocation = locations.find((l: any) => l.id === waypoint.locationId);

            waypointServices.forEach((service: any) => {
                const isLoadService = service.loads?.some((load: any) => load.loadId === loadId);
                if (!isLoadService) return;

                allWaypointServices.push({
                    ...service,
                    waypointType: 'waypoints',
                    waypointName: waypointLocation?.name || `${formatMessage(messages.waypoint)} ${waypointIndex + 1}`,
                    locationName: waypointLocation?.name || `${formatMessage(messages.waypoint)} ${waypointIndex + 1}`
                });
            });
        });

        const serviceRows = allWaypointServices.map((service: any, idx: number) => {
            const serviceObj = services.find((s: any) => s.id === service.serviceId);
            return {
                id: idx,
                waypoint: service.waypointName,
                service: `${serviceObj?.name} (${serviceObj?.code})` || '-',
            };
        });
        const columns = [
            {
                accessorKey: 'waypoint',
                header: formatMessage(messages.serviceWaypoint),
            },
            {
                accessorKey: 'service',
                header: formatMessage(messages.consignmentServices),
            },
        ];
        return (
            <MRTDataGridv2
                columns={columns}
                data={serviceRows}
                enableColumnActions={false}
                enableColumnFilters={false}
                enablePagination={false}
                enableSorting={false}
                enableBottomToolbar={false}
                enableTopToolbar={false}
                componentPadding='0'
                componentMargin='0px 0 16px 0'
                muiTableBodyRowProps={{
                    sx: {
                        height: '32px',
                        maxHeight: '32px',
                        minHeight: '32px',
                    },
                }}
                muiTableBodyCellProps={{
                    sx: {
                        height: '32px',
                        maxHeight: '32px',
                        minHeight: '32px',
                        paddingTop: 0,
                        paddingBottom: 0,
                        overflow: 'hidden',
                    },
                }}
                state={{ isLoading: false }}
                getRowId={(row: any) => row.id}
            />
        );
    }

    return (
        <Box sx={{ mb: '12px', mt: '0px' }}>
            <MRTDataGridv2
                leftHeadingComponent={
                    <Box sx={{ display: 'inline-flex', p: '0px' }}>
                        <TableSubTitleTypography>{formatMessage(messages.services)}</TableSubTitleTypography>
                    </Box>
                }
                componentPadding='0 0 8px 0'
                state={{ isLoading: false }}
                columns={columns}
                data={loadData ?? []}
                getRowId={(row) => row.id as any}
                enableColumnActions={false}
                enableColumnFilters={true}
                enablePagination={false}
                enableSorting={true}
                enableBottomToolbar={false}
                enableTopToolbar={false}
                muiTableBodyRowProps={{
                    sx: {
                        height: '32px',
                        maxHeight: '32px',
                        minHeight: '32px',
                    },
                }}
                muiTableBodyCellProps={{
                    sx: {
                        height: '32px',
                        maxHeight: '32px',
                        minHeight: '32px',
                        paddingTop: 0,
                        paddingBottom: 0,
                        overflow: 'hidden',
                    },
                }}
            />
        </Box>
    );
};

export default ConsignmentServiceTable;